import { useState, useEffect, useCallback } from 'react';
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Flag } from '@phosphor-icons/react';
import { Button } from '../ui/button';

const SIZE = 15; // odd number

const generateMaze = () => {
  const grid = Array.from({ length: SIZE }, () => Array(SIZE).fill(1));
  const carve = (x, y) => {
    grid[y][x] = 0;
    const dirs = [[0, -2], [0, 2], [-2, 0], [2, 0]].sort(() => Math.random() - 0.5);
    for (const [dx, dy] of dirs) {
      const nx = x + dx;
      const ny = y + dy;
      if (nx > 0 && ny > 0 && nx < SIZE - 1 && ny < SIZE - 1 && grid[ny][nx] === 1) {
        grid[y + dy / 2][x + dx / 2] = 0;
        carve(nx, ny);
      }
    }
  };
  carve(1, 1);
  grid[SIZE - 2][SIZE - 2] = 0;
  return grid;
};

export function MazeEscape({ onScore }) {
  const [maze, setMaze] = useState(generateMaze);
  const [pos, setPos] = useState({ x: 1, y: 1 });
  const [level, setLevel] = useState(1);
  const [moves, setMoves] = useState(0);

  const nextLevel = useCallback(() => {
    setMaze(generateMaze());
    setPos({ x: 1, y: 1 });
    setMoves(0);
  }, []);

  const step = useCallback((dx, dy) => {
    setPos((current) => {
      const nx = current.x + dx;
      const ny = current.y + dy;
      if (nx < 0 || ny < 0 || nx >= SIZE || ny >= SIZE || maze[ny][nx] === 1) return current;
      setMoves((m) => m + 1);
      if (nx === SIZE - 2 && ny === SIZE - 2) {
        setLevel((l) => {
          const reached = l + 1;
          onScore(l);
          return reached;
        });
        setTimeout(nextLevel, 250);
      }
      return { x: nx, y: ny };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maze, nextLevel]);

  useEffect(() => {
    const map = {
      ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0],
      w: [0, -1], s: [0, 1], a: [-1, 0], d: [1, 0]
    };
    const handler = (e) => {
      const move = map[e.key];
      if (move) {
        e.preventDefault();
        step(move[0], move[1]);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [step]);

  return (
    <div className="space-y-4" data-testid="game-maze-escape">
      <div className="flex items-center justify-between text-sm">
        <span>Level: <span className="text-amber-300 tabular-nums" data-testid="maze-level">{level}</span></span>
        <span className="text-muted-foreground text-xs">Moves: {moves}</span>
      </div>

      <div className="rounded-lg border border-white/10 bg-black/60 p-2">
        <div className="grid gap-[1px]" style={{ gridTemplateColumns: `repeat(${SIZE}, 1fr)` }} data-testid="maze-board">
          {maze.map((row, y) =>
            row.map((cell, x) => {
              const isPlayer = pos.x === x && pos.y === y;
              const isExit = x === SIZE - 2 && y === SIZE - 2;
              return (
                <div
                  key={`${x}-${y}`}
                  className={`aspect-square rounded-[2px] flex items-center justify-center ${
                    cell === 1 ? 'bg-white/10' : isPlayer ? 'bg-amber-300' : isExit ? 'bg-emerald-500/70' : 'bg-transparent'
                  }`}
                >
                  {isExit && !isPlayer && <Flag size={8} weight="fill" className="text-black/70" />}
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="flex flex-col items-center gap-2">
        <Button variant="outline" size="sm" className="rounded-full w-12" onClick={() => step(0, -1)} data-testid="maze-up"><ArrowUp size={16} /></Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-full w-12" onClick={() => step(-1, 0)} data-testid="maze-left"><ArrowLeft size={16} /></Button>
          <Button variant="outline" size="sm" className="rounded-full w-12" onClick={() => step(0, 1)} data-testid="maze-down"><ArrowDown size={16} /></Button>
          <Button variant="outline" size="sm" className="rounded-full w-12" onClick={() => step(1, 0)} data-testid="maze-right"><ArrowRight size={16} /></Button>
        </div>
      </div>

      <Button variant="outline" className="rounded-full w-full" onClick={nextLevel} data-testid="maze-new">
        New maze
      </Button>
    </div>
  );
}
